// src/components/coordinateGrid/CoordinateDisplay.tsx
/**
 * CoordinateDisplay Component
 * 
 * Shows the current cursor position in mathematical coordinates.
 * Displayed in the bottom-right corner of the canvas.
 * Updates in real-time as the cursor moves.
 * 
 * Phase 1: Core infrastructure
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../types/redux';
import { getGridInterval } from '../../types/coordinateGrid';

interface CoordinateDisplayProps {
  /** Container width in pixels */
  width: number;
  /** Container height in pixels */
  height: number;
  /** Pixels per unit on x-axis */
  scaleX: number;
  /** Pixels per unit on y-axis */
  scaleY: number;
  /** X offset (position of origin in pixels from left) */
  offsetX: number;
  /** Y offset (position of origin in pixels from top) */
  offsetY: number;
  /** Reference to the container element for mouse tracking */
  containerRef: React.RefObject<HTMLElement>;
  /** Whether to enable grid snapping */
  enableSnapping?: boolean;
  /** Snap threshold in pixels */
  snapThreshold?: number;
  /** Whether dark mode is active */
  isDark?: boolean;
}

/**
 * Snap a value to the nearest grid line if within threshold
 */
function snapToGrid(
  value: number,
  gridInterval: number,
  threshold: number,
  scale: number
): { value: number; snapped: boolean } {
  const nearestGridValue = Math.round(value / gridInterval) * gridInterval;
  const distanceInPixels = Math.abs(value - nearestGridValue) * scale;
  
  if (distanceInPixels <= threshold) {
    return { value: nearestGridValue, snapped: true };
  }
  
  return { value, snapped: false };
}

/**
 * Format coordinate value for display
 */
function formatCoordinate(value: number, snapped: boolean): string {
  // Round to avoid floating point display issues
  const rounded = Math.round(value * 1000) / 1000;
  
  // Show integers without decimals
  if (Number.isInteger(rounded)) {
    return rounded.toString();
  }
  
  // Show up to 2 decimal places
  return rounded.toFixed(2).replace(/\.?0+$/, '');
}

export const CoordinateDisplay: React.FC<CoordinateDisplayProps> = ({
  width,
  height,
  scaleX,
  scaleY,
  offsetX,
  offsetY,
  containerRef,
  enableSnapping = true,
  snapThreshold = 10,
  isDark = false,
}) => {
  const [position, setPosition] = useState<{ x: number; y: number } | null>(null);
  const [snappedX, setSnappedX] = useState(false);
  const [snappedY, setSnappedY] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  // Get grid settings from Redux
  const coordinateGrid = useSelector((state: RootState) => state.coordinateGrid);
  const gridInterval = getGridInterval(coordinateGrid.gridDensity);

  // Convert pixel position to math coordinates
  const pixelToMath = useCallback(
    (pixelX: number, pixelY: number): { x: number; y: number } => {
      const mathX = (pixelX - offsetX) / scaleX;
      const mathY = (offsetY - pixelY) / scaleY; // Y is flipped
      return { x: mathX, y: mathY };
    },
    [offsetX, offsetY, scaleX, scaleY]
  );

  // Handle mouse movement
  const handleMouseMove = useCallback(
    (event: MouseEvent) => {
      const container = containerRef.current;
      if (!container) return;

      const rect = container.getBoundingClientRect();
      const pixelX = event.clientX - rect.left;
      const pixelY = event.clientY - rect.top;

      // Check if mouse is within bounds
      if (pixelX < 0 || pixelX > width || pixelY < 0 || pixelY > height) {
        setIsVisible(false);
        return;
      }

      setIsVisible(true);

      // Convert to math coordinates
      let { x, y } = pixelToMath(pixelX, pixelY);

      // Apply snapping if enabled
      let snappedToX = false;
      let snappedToY = false;

      if (enableSnapping && coordinateGrid.showGrid) {
        const snapX = snapToGrid(x, gridInterval, snapThreshold, scaleX);
        const snapY = snapToGrid(y, gridInterval, snapThreshold, scaleY);
        
        x = snapX.value;
        y = snapY.value;
        snappedToX = snapX.snapped;
        snappedToY = snapY.snapped;
      }

      setPosition({ x, y });
      setSnappedX(snappedToX);
      setSnappedY(snappedToY);
    },
    [
      containerRef,
      width,
      height,
      pixelToMath,
      enableSnapping,
      coordinateGrid.showGrid,
      gridInterval,
      snapThreshold,
      scaleX,
      scaleY,
    ]
  );

  // Handle mouse leave
  const handleMouseLeave = useCallback(() => {
    setIsVisible(false);
  }, []);

  // Attach event listeners
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    container.addEventListener('mousemove', handleMouseMove);
    container.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      container.removeEventListener('mousemove', handleMouseMove);
      container.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [containerRef, handleMouseMove, handleMouseLeave]);

  // Styles
  const containerStyle: React.CSSProperties = useMemo(
    () => ({
      position: 'absolute',
      bottom: 12,
      right: 12,
      padding: '6px 12px',
      borderRadius: 6,
      backgroundColor: isDark ? 'rgba(31, 41, 55, 0.9)' : 'rgba(255, 255, 255, 0.95)',
      border: `1px solid ${isDark ? '#374151' : '#E5E7EB'}`,
      boxShadow: isDark 
        ? '0 2px 8px rgba(0, 0, 0, 0.3)' 
        : '0 2px 8px rgba(0, 0, 0, 0.1)',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      fontSize: 13,
      fontWeight: 500,
      color: isDark ? '#E5E7EB' : '#374151',
      pointerEvents: 'none',
      userSelect: 'none',
      opacity: isVisible ? 1 : 0,
      transition: 'opacity 0.15s ease-out',
      zIndex: 100,
      display: 'flex',
      alignItems: 'center',
      gap: 4,
    }),
    [isDark, isVisible]
  );

  const labelStyle: React.CSSProperties = {
    color: isDark ? '#9CA3AF' : '#6B7280',
    marginRight: 2,
  };

  const valueStyle = (isSnapped: boolean): React.CSSProperties => ({
    color: isSnapped 
      ? (isDark ? '#60A5FA' : '#2563EB')
      : (isDark ? '#E5E7EB' : '#374151'),
    fontFamily: 'ui-monospace, monospace',
    minWidth: 45,
    textAlign: 'right',
  });

  const separatorStyle: React.CSSProperties = {
    color: isDark ? '#6B7280' : '#9CA3AF',
    margin: '0 4px',
  };

  if (!position) {
    return null;
  }

  return (
    <div style={containerStyle} className="coordinate-display">
      <span style={labelStyle}>(</span>
      <span style={valueStyle(snappedX)}>
        {formatCoordinate(position.x, snappedX)}
      </span>
      <span style={separatorStyle}>,</span>
      <span style={valueStyle(snappedY)}>
        {formatCoordinate(position.y, snappedY)}
      </span>
      <span style={labelStyle}>)</span>
      
      {/* Snap indicator */}
      {(snappedX || snappedY) && (
        <span 
          style={{ 
            marginLeft: 6, 
            fontSize: 10, 
            color: isDark ? '#60A5FA' : '#2563EB',
            opacity: 0.8,
          }}
          title="Snapped to grid"
        >
          ⊕
        </span>
      )}
    </div>
  );
};

export default CoordinateDisplay;
